import { useState } from "react";
import { z } from "zod";
import { PALETTE, FONT_BODY, FONT_MONO, track } from "@/components/cobrain/tokens";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { estimateNote, readServicesEstimate } from "@/hooks/useServicesEstimate";

/* Lower-commitment alternative to the call: leave a name + email and we
   send the audit outline. Same close-lead endpoint, distinct leadSource. */

const CLOSE_LEAD_ENDPOINT =
  "https://dvddfvigkzikieuqrtcn.supabase.co/functions/v1/close-lead";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR2ZGRmdmlna3ppa2lldXFydGNuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY4NzAxMTgsImV4cCI6MjA5MjQ0NjExOH0.DiSDka0k9Ig6jYjC1xiXfgiQO6KPNYh5BTUGfeZbfc8";

const Schema = z.object({
  name: z.string().trim().min(1, "Your name, please").max(80),
  email: z.string().trim().email("Enter a valid email").max(255),
});

export function AuditOutlineForm({ context, source = "services_audit_outline" }: {
  context?: string; source?: string;
} = {}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = Schema.safeParse({ name, email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Check the fields");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(CLOSE_LEAD_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          firstName: parsed.data.name,
          email: parsed.data.email,
          source,
          leadSource: "Services Audit Outline",
          note: [
            "Requested the AI readiness audit outline from /services (no call booked yet).",
            context,
            estimateNote(readServicesEstimate()),
          ].filter(Boolean).join(" "),
          pageUrl: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : "",
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.ok === false) {
        throw new Error(typeof json?.error === "string" ? json.error : "Couldn't send that. Try again in a moment.");
      }
      setDone(true);
      track("services_outline_submit", { source });
    } catch (err: any) {
      setError(err?.message || "Couldn't send that. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "12px 14px", borderRadius: 12,
    background: "#fff", border: `1px solid ${PALETTE.line}`,
    color: PALETTE.ink, fontSize: 15, fontFamily: FONT_BODY, minHeight: 48,
  };

  if (done) {
    return (
      <div style={{ fontSize: 15, lineHeight: 1.6, color: PALETTE.ink2 }}>
        <strong style={{ color: PALETTE.ink }}>Sent.</strong> The outline is on its way — it lists exactly
        what we look at and what you get back. Want it walked through instead?{" "}
        <button
          type="button"
          onClick={() => openCallMeNow("services_outline_success")}
          style={{ background: "none", border: 0, padding: 0, color: PALETTE.amber, fontWeight: 600, cursor: "pointer", fontSize: 15, fontFamily: FONT_BODY }}
        >
          Talk to us now →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, marginBottom: 8 }}>
        Not ready to talk yet?
      </div>
      <p style={{ margin: "0 0 14px", fontSize: 15, lineHeight: 1.55, color: PALETTE.ink2 }}>
        Get the audit outline by email — what we look at, what you get back, and how long it takes. No call required.
      </p>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }} className="svc-outline-grid">
        <label style={{ display: "block" }}>
          <span className="sr-only">Your name</span>
          <input
            style={inputStyle}
            placeholder="Your name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label style={{ display: "block" }}>
          <span className="sr-only">Work email</span>
          <input
            style={inputStyle}
            placeholder="Work email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
      </div>
      {error && (
        <p role="alert" style={{ margin: "10px 0 0", fontSize: 13.5, color: "#8a2f24" }}>{error}</p>
      )}
      <button
        type="submit"
        disabled={busy}
        style={{
          marginTop: 12, width: "100%", minHeight: 48, borderRadius: 999,
          border: `1px solid ${PALETTE.ink}`, background: "transparent",
          color: PALETTE.ink, fontSize: 15, fontWeight: 600, fontFamily: FONT_BODY,
          cursor: busy ? "wait" : "pointer", opacity: busy ? 0.65 : 1,
        }}
      >
        {busy ? "Sending…" : "Send me the audit outline"}
      </button>
      <p style={{ margin: "10px 0 0", fontSize: 12, color: PALETTE.muted }}>
        One email with the outline. No sequence, no drip.
      </p>
    </form>
  );
}

export default AuditOutlineForm;
